import React, { useCallback } from 'react';
import { osStr, isDesktop } from '@trello/browser';
import { Sky25 } from '@trello/colors';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { usesEnglish } from '@trello/locale';
import { memberId } from '@trello/session-cookie';

import {
  useDesktopSurveyBannerDismissedMessagesQuery,
  useDesktopSurveyBannerDismissMessageMutation,
} from '@trello/graphql/generated';

import Banner from 'app/common/components/banner/banner';
import styles from './DesktopSurveyBanner.less';

const ONE_TIME_MESSAGE_NAME = 'desktop-user-survey-banner';

// These strings are intentionally *not* localized, because we're creating a UI
// that links to a Typeform survey (the survey itself can't be localized, so we
// only show this component to English-speaking users)
const DESKTOP_SURVEY_QUESTION = 'How would you rate your desktop experience?';
const DESKTOP_SURVEY_LINK = 'https://trello.typeform.com/to/gsgaHP';
const POSITIVE_LINK_TEXT = 'Excellent!';
const NEGATIVE_LINK_TEXT = 'We need to talk';

const getDesktopSurveyLink = (answer: boolean): string => {
  return `${DESKTOP_SURVEY_LINK}?memberid=${memberId}&os=${osStr}&answer=${answer}`;
};

export const DesktopSurveyBanner = () => {
  const [
    addOneTimeMessagesDismissed,
  ] = useDesktopSurveyBannerDismissMessageMutation();

  const desktop = isDesktop();
  const englishPreferred = usesEnglish();

  const desktopBannerSurveyEnabled = useFeatureFlag(
    'fep.desktop-survey-banner',
    false,
  );

  const { data } = useDesktopSurveyBannerDismissedMessagesQuery({
    variables: { memberId: memberId! },
    skip: !memberId,
  });

  const oneTimeMessagesDismissed =
    data && data.member && data.member.oneTimeMessagesDismissed;

  // default to true if oneTimeMessagesDismissed isn't ready yet
  const hasShownBanner = oneTimeMessagesDismissed
    ? oneTimeMessagesDismissed.some(
        (message: string) => message === ONE_TIME_MESSAGE_NAME,
      )
    : true;

  const showBanner =
    desktop &&
    englishPreferred &&
    desktopBannerSurveyEnabled &&
    !hasShownBanner;

  const onDismiss = useCallback(
    () => {
      // we should never reach this without a real memberId
      if (!memberId) {
        return;
      }

      addOneTimeMessagesDismissed({
        variables: {
          memberId,
          messageId: ONE_TIME_MESSAGE_NAME,
        },
        optimisticResponse: {
          addOneTimeMessagesDismissed: {
            id: memberId,
            oneTimeMessagesDismissed: (oneTimeMessagesDismissed || []).concat(
              ONE_TIME_MESSAGE_NAME,
            ),
            __typename: 'Member',
          },
        },
      });
    },
    [addOneTimeMessagesDismissed, oneTimeMessagesDismissed],
  );

  return showBanner ? (
    <div>
      <Banner
        onDismiss={onDismiss}
        bannerColor={Sky25}
        className={styles.banner}
        roundedCorners
        alignCloseTop
      >
        <div className={styles.desktopSurveyBannerContainer}>
          <div className={styles.text}>
            {DESKTOP_SURVEY_QUESTION}
            <a
              className={styles.link}
              onClick={onDismiss}
              href={getDesktopSurveyLink(true)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {POSITIVE_LINK_TEXT}
            </a>
            <a
              className={styles.link}
              onClick={onDismiss}
              href={getDesktopSurveyLink(false)}
              rel="noopener noreferrer"
              target="_blank"
            >
              {NEGATIVE_LINK_TEXT}
            </a>
          </div>
        </div>
      </Banner>
    </div>
  ) : null;
};
